'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import Footer from '@/components/footer'
import Navigation from '@/components/navigation'
import { Alert } from '@/components/ui/alert'
import Contact from '@/components/contact'

export default function ContentUsePage(): React.ReactElement {
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    subject: "", // Added subject field
    message: "",
    assetUrl: "", // New field
    socialHandle: "", // New field
  })

  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: null as string | null,
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    })
  }

  // Update the form validation to make Asset URL optional
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Check for mandatory fields
    if (!formState.name || !formState.email || !formState.message || !formState.socialHandle) {
      setStatus({
        loading: false,
        success: false,
        error: 'Please fill out all mandatory fields: Name, Email, Message, and Social Media Handle.',
      })
      return
    }

    // Validate social media handle as a full URL
    const urlPattern = /^(https?:\/\/)?([\w.-]+)+(:\d+)?(\/([\w/_.]*)?)?$/
    if (!urlPattern.test(formState.socialHandle)) {
      setStatus({
        loading: false,
        success: false,
        error: 'Please provide a valid URL for the Social Media Handle.',
      })
      return
    }

    // Prefix the subject with "[RG-ContentUse]" and the person's name
    const prefixedSubject = formState.subject ? `[RG-ContentUse] ${formState.subject} - ${formState.name}` : `[RG-ContentUse] ${formState.name}`

    setStatus({ loading: true, success: false, error: null })

    try {
      const response = await fetch('https://9k1xtga6i1.execute-api.us-east-1.amazonaws.com/prod/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formState.name,
          email: formState.email,
          subject: prefixedSubject,
          message: formState.message,
          assetUrl: formState.assetUrl || null, // Make Asset URL optional
          socialHandle: formState.socialHandle,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to send message')
      }

      setStatus({ loading: false, success: true, error: null })
      setFormState({ name: "", email: "", subject: "", message: "", assetUrl: "", socialHandle: "" }) // Reset new fields
    } catch (error) {
      setStatus({
        loading: false,
        success: false,
        error: 'Failed to send message. Please try again later.',
      })
    }
  }

  return (
    <>
      <Navigation solid />
      <div className="container mx-auto px-4 py-8 min-h-screen mt-24">
        <div className="max-w-3xl mx-auto">
          <div className="relative mb-8">
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-4">
              Community-Friendly Content Use Guidelines 🌱
            </h1>
            <div className="h-1 w-16 bg-amber-500"></div>
          </div>

          <div className="prose prose-invert max-w-none">
            <p className="text-lg sm:text-xl md:text-2xl text-white/80 mb-8">
              My goal is to share useful insights and original frameworks that help others grow — especially in the evolving AI and enterprise space.
              If you've found value in my content, I genuinely appreciate you sharing it. To help us protect the integrity of the work while keeping things open for the community, here are a few simple guidelines:
            </p>

            <div className="space-y-8">
              <section className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 sm:p-8 hover:border-slate-700 transition-colors">
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white mb-4 sm:mb-6">Content Use License</h2>
                <p className="mb-4 sm:mb-6 text-base sm:text-lg text-white/80">
                  All original content by Rakesh Gohel is shared under:
                </p>
                <div className="bg-slate-950 p-4 sm:p-6 rounded-lg mb-4 sm:mb-6 border border-slate-800">
                  <p className="font-bold text-amber-500 mb-2">CC BY-NC 4.0</p>
                  <p className="text-white/80">Attribution–NonCommercial</p>
                </div>
                <p className="font-medium text-white mb-4">You're free to:</p>
                <ul className="list-disc ml-4 sm:ml-6 space-y-2 mb-4 sm:mb-6 text-base sm:text-lg text-white/80">
                  <li>Share — repost or redistribute the content in any format</li>
                  <li>Adapt — remix, transform, or build upon the material</li>
                </ul>
                <p className="text-base sm:text-lg text-white/80 mb-3">As long as you follow these terms:</p>
                <ul className="list-disc ml-4 sm:ml-6 space-y-2 text-base sm:text-lg text-white/80">
                  <li>Give credit to Rakesh Gohel</li>
                  <li>Don't use the content commercially</li>
                  <li>Indicate if you've made any changes</li>
                  <li>Link back to the original content when possible</li>
                </ul>
              </section>

              <section className="bg-slate-900/50 border border-slate-800 rounded-xl p-8 hover:border-slate-700 transition-colors">
                <h2 className="text-2xl font-bold tracking-tight text-white mb-6">What You're Welcome to Do</h2>
                <ul className="list-disc pl-6 space-y-3 text-base sm:text-lg text-white/80">
                  <li>Repost my content as-is on LinkedIn, Twitter, newsletters, etc., with proper credit</li>
                  <li>Use ideas to spark discussion in your own voice (just tag me!)</li>
                  <li>Reference or cite in presentations, internal team docs, and learning materials</li>
                </ul>
              </section>

              <section className="bg-slate-900/50 border border-slate-800 rounded-xl p-8 hover:border-slate-700 transition-colors">
                <h2 className="text-2xl font-bold tracking-tight text-white mb-6">Attribution Requirements</h2>
                <div className="bg-slate-950 p-6 rounded-lg border border-slate-800">
                  <p className="mb-3 text-lg font-medium text-white">"Created by Rakesh Gohel"</p>
                  <p className="text-white/60">Tag: @rakeshgohel01 or link to the original post/source.</p>
                </div>
              </section>

              <section className="bg-slate-900/50 border border-slate-800 rounded-xl p-8 hover:border-slate-700 transition-colors">
                <h2 className="text-2xl font-bold tracking-tight text-white mb-6">Not for Commercial Use</h2>
                <p className="mb-6 text-base sm:text-lg text-white/80">Please don't use my content in:</p>
                <ul className="list-disc pl-6 space-y-3 text-base sm:text-lg text-white/80">
                  <li>Paid products (courses, newsletters, decks, etc.)</li>
                  <li>Consulting deliverables or institutional knowledge libraries</li>
                  <li>AI model training, dataset building, or retrieval-based tools</li>
                </ul>
              </section>

              <section className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 sm:p-8 hover:border-slate-700 transition-colors">
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white mb-4">Want to Collaborate or License?</h2>
                <p className="mb-6 text-base sm:text-lg text-white/80">Fill out the form below to discuss:</p>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {status.error && (
                    <Alert variant="destructive">
                      <p>{status.error}</p>
                    </Alert>
                  )}
                  {status.success && (
                    <Alert className="bg-green-500/10 text-green-500 border-green-500/50">
                      <p>Thank you for your message! We'll get back to you soon.</p>
                    </Alert>
                  )}
                  <div className="space-y-3">
                    <label htmlFor="name" className="text-white/90 text-base font-medium block">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formState.name}
                      onChange={handleChange}
                      required
                      className="w-full px-6 py-4 bg-slate-800/30 border-2 border-slate-700/50 rounded-lg text-white text-lg focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 transition-all"
                      placeholder="Your full name"
                    />
                  </div>
                  <div className="space-y-3">
                    <label htmlFor="email" className="text-white/90 text-base font-medium block">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formState.email}
                      onChange={handleChange}
                      required
                      className="w-full px-6 py-4 bg-slate-800/30 border-2 border-slate-700/50 rounded-lg text-white text-lg focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 transition-all"
                      placeholder="Your email address"
                    />
                  </div>
                  <div className="space-y-3">
                    <label htmlFor="subject" className="text-white/90 text-base font-medium block">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formState.subject}
                      onChange={handleChange}
                      className="w-full px-6 py-4 bg-slate-800/30 border-2 border-slate-700/50 rounded-lg text-white text-lg focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 transition-all"
                      placeholder="Subject of your message"
                    />
                  </div>
                  <div className="space-y-3">
                    <label htmlFor="message" className="text-white/90 text-base font-medium block">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formState.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-6 py-4 bg-slate-800/30 border-2 border-slate-700/50 rounded-lg text-white text-lg focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 transition-all resize-none"
                      placeholder="Please describe how you'd like to use the content"
                    ></textarea>
                  </div>
                  <div className="space-y-3">
                    <label htmlFor="assetUrl" className="text-white/90 text-base font-medium block">
                      Asset URL
                    </label>
                    <input
                      type="text"
                      id="assetUrl"
                      name="assetUrl"
                      value={formState.assetUrl}
                      onChange={handleChange}
                      className="w-full px-6 py-4 bg-slate-800/30 border-2 border-slate-700/50 rounded-lg text-white text-lg focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 transition-all"
                      placeholder="Enter the URL of the asset"
                    />
                  </div>
                  <div className="space-y-3">
                    <label htmlFor="socialHandle" className="text-white/90 text-base font-medium block">
                      Social Media Handle (Full URL) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      id="socialHandle"
                      name="socialHandle"
                      value={formState.socialHandle}
                      onChange={handleChange}
                      required
                      className="w-full px-6 py-4 bg-slate-800/30 border-2 border-slate-700/50 rounded-lg text-white text-lg focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 transition-all"
                      placeholder="https://your-social-media-profile.com"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={status.loading}
                    className={`w-full py-4 px-8 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg text-lg font-semibold transition-all flex items-center justify-center gap-2 ${status.loading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                  >
                    {status.loading ? (
                      <span className="inline-flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                </form>
              </section>
            </div>
          </div>
        </div>
      </div>
      <Contact />
      <Footer />
    </>
  )
}
